import React, { Fragment, useMemo, useState } from 'react';
import "./formulaReport.scss";
import { ReportFooter } from '../ReportComponents/ReportFooter';
import { ReportHeader } from '../ReportComponents/ReportHeader';
import { ReportTitle } from '../ReportComponents/ReportTitle';
import { StageDischargeGraphReport } from 'cd-eft/components/EFH2/Formulas/FormulaComponents/Common/StageDischargeGraph';
import { arrayTo2d } from '../WDTConstructionReport/WDTConstructionReport';
import { smartFormatDecimal } from 'cd-eft/utils/decimalFormatting';
import { updateToTenChars } from 'cd-eft/utils/mathUtilities';

interface FormulaReportValueObject {
    label: string;
    value?: any;
    section?: boolean;
}
export interface FormulaReportProps {
    images: any;
    inputs: FormulaReportValueObject[];
    outputs: FormulaReportValueObject[];
    formData: any;
    formulaName: string;
    formDescription: string;
    tableData?: boolean;
    stageDischarge?: boolean;
    stageDischargeData?: any;
    windowOptions?: any;
}

export const FormulaReport = (props: FormulaReportProps) => {
    const [excessInputItems, setExcessInputItems] = useState<any>([])
    const { images, inputs, outputs, formData, formulaName, formDescription, tableData, stageDischarge, stageDischargeData } = props;
    const numberOfItems = 23;

    const getPageMargins = () => {
        const pageMargin = 0.5;
        return `@page { margin: ${pageMargin}in !important; }`;
    };

    function handleExcessInputs(inpArr) {
        if (inpArr.length > numberOfItems) {
            setExcessInputItems(inputs.splice(numberOfItems))
        }
        return excessInputItems;
    }

    React.useEffect(() => {
        handleExcessInputs(inputs)
    }, [inputs])

    // Check if this is Hydrology Calculator manual input mode (has "24-hr Rain (in)" vs "24-hr Rain")
    const isHydrologyManualInput = useMemo(() => {
        if (formulaName !== 'Hydrology Calculator' || !outputs.length) return false;
        const headers = outputs[0].label.split(', ');
        return headers.includes('24-hr Rain (in)');
    }, [formulaName, outputs]);

    const parseTableData = useMemo(() => {
        if (!outputs || outputs.length === 0) {
            return <div className="formula-report-results">No table data available</div>;
        }

        const headers = outputs[0].label.split(', ');
        let bodyData = outputs.map(e => Object.values(e.value!));
        if (formulaName === 'Subsurface Drainage') {
            bodyData = bodyData.map(e => {
                const [acers, acresAvail, diameter, qavail, qreq, slope, velocity] = e;
                return [acers, acresAvail, slope, velocity, diameter, qavail, qreq];
            })
        }

        // Add Time of Concentration row for Hydrology Calculator database mode
        if (formulaName === 'Hydrology Calculator' && !isHydrologyManualInput) {
            // Get the Time of Concentration value from inputs if available
            const timeConcInput = inputs.find(input => input.label === 'Time of Concentration (hr)');
            if (timeConcInput?.value) {
                const timeConcRow = [`Time of Concentration (hrs): ${timeConcInput.value}`];
                bodyData.push(timeConcRow);
            }
        }

        const tableBodyArray: any[] = bodyData.reduce((result, item, index) => {
            if (index % numberOfItems === 0) {
                result.push(bodyData.slice(index, index + numberOfItems));
            }
            return result;
        }, []);

        return (
            <div className='formula-report-results'>
                {tableBodyArray.map((array, tableIndex) => (
                    <div
                        key={`table-${tableIndex}`}
                        className={`${formulaName === 'Subsurface Drainage' || isHydrologyManualInput ? 'summary-subsurface subsurface-breaker' : formulaName === 'Hydrology Calculator' ? 'hydrology-calculator-results hydrology-breaker' : 'usa-summary-box table-breaker'}`}
                        role="region"
                        aria-labelledby="summary-box-key-information"
                        tabIndex={7}
                    >
                        <div className={`usa-summary-box__body ${formulaName === 'Hydrology Calculator' && isHydrologyManualInput ? 'manual-results-mode' : ''}`}>
                            {/* Only show section header for non-Hydrology Calculator database mode */}
                            {!(formulaName === 'Hydrology Calculator' && !isHydrologyManualInput) && (
                                <h2 className="usa-summary-box__heading formula-report-section-label">Results</h2>
                            )}
                            <div className={`usa-summary-box__text ${formulaName === 'Hydrology Calculator' && !isHydrologyManualInput ? 'hydrology-database-mode' : ''}`}>
                                <table className={`usa-table usa-table--stacked usa-table--striped print-table ${formulaName === 'Subsurface Drainage' || isHydrologyManualInput ? 'subsurface' : formulaName === 'Hydrology Calculator' ? 'hydrology-table' : ''}`}>
                                    <thead>
                                        {formulaName === 'Subsurface Drainage' || isHydrologyManualInput ?
                                            <tr>
                                                <th colSpan={2} className="sections-heading">Input</th>
                                                <th colSpan={isHydrologyManualInput ? 2 : 5} className="results-header sections-heading">Results</th>
                                            </tr> :
                                            formulaName === 'Hydrology Calculator' ?
                                                <tr>
                                                    <th colSpan={headers.length} className="hydrology-header sections-heading">Results</th>
                                                </tr> : null
                                        }
                                        <tr className={`${formulaName === 'Subsurface Drainage' || isHydrologyManualInput ? 'results-header' : formulaName === 'Hydrology Calculator' ? 'hydrology-header' : ''}`}>

                                            {headers.map((header, index) =>
                                                <th className={`${formulaName === 'Subsurface Drainage' ? index > 1 ? 'results-header' : null : isHydrologyManualInput ? index > 1 ? 'results-header' : null : formulaName === 'Hydrology Calculator' ? 'hydrology-header' : null}`} key={`header-${index}`} aria-readonly={true}>{header}</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {array.map((row, rowIndex) => {
                                            const isTimeConcRow = typeof row[0] === 'string' &&
                                                row[0].includes('Time of Concentration');

                                            return (
                                                <tr key={`row-${tableIndex}-${rowIndex}`}>
                                                    {row.map((data, cellIndex) => {
                                                        if (isTimeConcRow && cellIndex === 0) {
                                                            // Time of Concentration row spans full width
                                                            return (
                                                                <td
                                                                    key={`cell-${tableIndex}-${rowIndex}-${cellIndex}`}
                                                                    colSpan={headers.length}
                                                                    style={{ textAlign: 'left' }}
                                                                    aria-readonly={true}
                                                                    role='gridcell'
                                                                    className={`${formulaName === 'Subsurface Drainage' || isHydrologyManualInput ? cellIndex > (1) ? 'results-header' : '' : formulaName === 'Hydrology Calculator' ? 'hydrology-cell' : null}`}
                                                                >
                                                                    <strong>{data as React.ReactNode}</strong>
                                                                </td>
                                                            );
                                                        } else if (isTimeConcRow && cellIndex > 0) {
                                                            // Skip other cells in Time of Concentration row
                                                            return null;
                                                        } else {
                                                            return (
                                                                <td className={`${formulaName === 'Subsurface Drainage' ? cellIndex > 1 ? 'results-header' : '' : isHydrologyManualInput ? cellIndex > 1 ? 'results-header' : '' : formulaName === 'Hydrology Calculator' ? 'hydrology-cell' : null}`} key={`cell-${tableIndex}-${rowIndex}-${cellIndex}`} aria-readonly={true} role='gridcell'>{data as React.ReactNode}</td>
                                                            );
                                                        }
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }, [outputs, isHydrologyManualInput, formulaName]);

    const parseListData = useMemo(() => {
        return (
            <div className="col formula-report-results">
                <div
                    className='usa-summary-box'
                    role="region"
                    aria-labelledby="summary-box-key-information"
                    tabIndex={7}
                >
                    <div className={`usa-summary-box__body ${isHydrologyManualInput ? 'manual-results-mode' : ''}`}>
                        <h2 className="usa-summary-box__heading formula-report-section-label">Results</h2>
                        <div className='usa-summary-box__text'>
                            <ul className="usa-list usa-list--unstyled">
                                {outputs ? outputs.map((output, index) => {
                                    if (index % 25 === 0 && index > 0) {
                                        return (
                                            <li key={`output-${output.label}`} className='breaker'>{output.label}: {trailingZero(output.label, output.value, formulaName, 'results')}</li>
                                        )
                                    }
                                    else {
                                        return (
                                            <li key={`output-${output.label}`}>{output.label}: {trailingZero(output.label, output.value, formulaName, 'results')}</li>
                                        )
                                    }
                                }) : null}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        )
    }, [outputs])

    const parseGraphData = useMemo(() => {
        if (stageDischarge === undefined || !stageDischarge) {
            return;
        }
        const splitGraphArray = arrayTo2d(stageDischargeData.stageDischargeData, 25);
        return (
            <Fragment>
                <StageDischargeGraphReport windowOptions={props.windowOptions} targetDivId='formula-report-graph-div' plotData={props.stageDischargeData} forReport={true} splitTableData={splitGraphArray} />
            </Fragment>
        )
    }, [stageDischargeData])

    return (
        <div className="formula-report-container">
            <style>{getPageMargins()}</style>
            <ReportHeader />
            <ReportTitle title={formulaName} />
            {formulaName !== 'Hydrology Calculator' && (
                <div className='row formula-report-images' tabIndex={5} >
                    <img key={`image-${0}`} src={images[0]} alt={formDescription} />
                </div>
            )}
            <div className={`row formula-report-content ${formulaName === 'Hydrology Calculator' ? 'hydrology-layout' : ''} ${formulaName === 'Hydrology Calculator' && !isHydrologyManualInput ? 'hydrology-database-layout' : ''}`} style={formulaName === 'Hydrology Calculator' ? { marginTop: '2rem' } : {}}>
                <div className={tableData && formulaName !== 'Hydrology Calculator' ? "formula-report-inputs formula-report-inputs-table-format" : formulaName === 'Hydrology Calculator' ? "col-6 formula-report-inputs hydrology-inputs" : "col formula-report-inputs"}>
                    <div
                        className='usa-summary-box'
                        role="region"
                        aria-labelledby="summary-box-key-information"

                    >
                        <div className={`usa-summary-box__body ${formulaName === 'Hydrology Calculator' && isHydrologyManualInput ? 'manual-input-mode' : ''}`} tabIndex={6}>
                            <h2 className="usa-summary-box__heading formula-report-section-label">Input</h2>
                            <div className='usa-summary-box__text'>
                                <ul className="usa-list usa-list--unstyled">
                                    {/* Might need to tweak this and just create another ul following a page break */}
                                    {inputs ? inputs.map((input, index) => {
                                        // let reqIndex = formulaName === 'Surface Inlet'? 32 : 25 ;
                                        if (index % numberOfItems === 0 && index > 0) {
                                            return (
                                                <li key={`input-${input.label}`} className='breaker'>
                                                    {input.section ? <h3 className="formula-report-section-label">{input.label}</h3> : <>{input.label}: {trailingZero(input.label, input.value, formulaName, 'inputs')}</>}
                                                </li>
                                            )
                                        }
                                        else {
                                            return (
                                                <li key={`input-${input.label}`}>
                                                    {input.section ? <h3 className="formula-report-section-label h5 mt-2">{input.label}</h3> : <>{input.label}: {trailingZero(input.label, input.value, formulaName, 'inputs')}</>}
                                                </li>
                                            )
                                        }
                                    }) : null}
                                </ul>
                            </div>
                        </div>

                    </div>
                    {excessInputItems && formulaName === 'Surface Inlet' && excessInputItems.length ?
                        <div
                            className='usa-summary-box table-breaker'
                            role="region"
                            aria-labelledby="summary-box-key-information"
                        >
                            <div className='usa-summary-box__body' tabIndex={7}>
                                <h2 className="usa-summary-box__heading formula-report-section-label">Input</h2>
                                <div className='usa-summary-box__text'>
                                    <ul className="usa-list usa-list--unstyled">
                                        {excessInputItems ? excessInputItems.map((input, index) => {
                                            // let reqIndex = formulaName === 'Surface Inlet'? 32 : 25 ;
                                            if (index % numberOfItems === 0 && index > 0) {
                                                return (
                                                    <li key={`input-${input.label}`} className=''>
                                                        {input.section ? <h3 className="formula-report-section-label">{input.label}</h3> : <>{input.label}: {trailingZero(input.label, input.value, formulaName, 'inputs')}</>}
                                                    </li>
                                                )
                                            }
                                            else {
                                                return (
                                                    <li key={`input-${input.label}`}>
                                                        {input.section ? <h3 className="formula-report-section-label h5 mt-2">{input.label}</h3> : <>{input.label}: {trailingZero(input.label, input.value, formulaName, 'inputs')}</>}
                                                    </li>
                                                )
                                            }
                                        })
                                            : null}
                                    </ul>
                                </div>
                            </div>
                        </div> : null}
                </div>

                {/* Following result boxes */}
                {formulaName === 'Hydrology Calculator' ? (
                    <div className="col-6 hydrology-results">
                        {tableData ? parseTableData : parseListData}
                    </div>
                ) : (
                    tableData ? parseTableData : parseListData
                )}
            </div>
            {stageDischarge ? parseGraphData : null}
            <ReportFooter
                name={formData.name}
                location={formData.location}
                practice={formData.practice}
                description={formData.description}
                designedByDate={formData.designedByDate}
                designedByName={formData.designedByName}
            />
            {/* <div className='formula-page-count'>Page </div> */}
        </div>
    )
}

function trailingZero(label: string, value, formulaName, section?: 'inputs' | 'results') {
    if ((value.toString() as string).match(/[^\d\.\-\+]/g) || isNaN(parseFloat(value.toString()))) {
        return value;
    }
    const fourDecimals = 4;
    const fiveDecimals = 5;
    const twoDecimals = 2;
    const name = label.toLowerCase();

    if (name.includes('bed slope')) {
        return smartFormatDecimal(+value, fourDecimals);
    }
    else if (name.includes('pipe slope')) {
        return smartFormatDecimal(+value, fourDecimals);
    }
    else if (name.includes('manning')) {
        return smartFormatDecimal(+value, fourDecimals);
    }
    else if (formulaName === "Gravity Flow in a Circular Section") {
        if (section === 'inputs') {
            return smartFormatDecimal(+value, fiveDecimals);
        }
        else {
            return updateToTenChars(+value);
        }
    }
    else {
        return smartFormatDecimal(+value, twoDecimals);
    }
}